/**
 * src/app/app-routing.module.ts
 *
 * @author Bryan Henry <bryan@payfully.co>
 * @date 4/27/2021
 * @copyright Payfully, 2021
 */

import { NgModule } from '@angular/core';
import { RouterModule, Routes } from '@angular/router';

import { DashboardBrokerComponent } from './pages/dashboard/pages/dashboard-broker/dashboard-broker.component';
import { DashboardNewBankAccountComponent } from './pages/dashboard/pages/dashboard-new-bank-account/dashboard-new-bank-account.component';
import { DashboardPayrollSettingsComponent } from './pages/dashboard/pages/dashboard-payroll-settings/dashboard-payroll-settings.component';
import { TaxesComponent } from './pages/dashboard/pages/taxes/taxes.component';

const routes: Routes = [
  {
    path: 'signup-old',
    loadChildren: () =>
      import('./modules/signup/signup.module').then((m) => m.SignupModule),
  },
  {
    path: 'signup-v1-verify-phone',
    loadChildren: () =>
      import('./modules/signupFixV1/signup-fix-v1.module').then(
        (m) => m.SignupFixV1Module,
      ),
  },
  {
    path: 'signup',
    loadChildren: () =>
      import('./modules/signupV2/signup-v2.module').then(
        (m) => m.SignupV2Module,
      ),
  },
  {
    path: 'onboarding',
    loadChildren: () =>
      import('./modules/onboarding/onboarding.module').then(
        (m) => m.OnboardingModule,
      ),
  },
  {
    path: 'invite-signup',
    loadChildren: () =>
      import('./modules/invite-signup-new/signup.module').then(
        (m) => m.InviteSignupModule,
      ),
  },
  {
    path: 'invite-signup-old',
    loadComponent: () =>
      import('./pages/invite-signup/invite-signup.component').then(
        (m) => m.InviteSignupComponent,
      ),
  },
  {
    path: 'invite-agent',
    loadChildren: () =>
      import('./modules/invite-agent/invite-agent.module').then(
        (m) => m.InviteAgentModule,
      ),
  },
  {
    path: 'request-to-join-brokerage',
    loadChildren: () =>
      import('./modules/request-to-join-partner/request-to-join-partner.module').then(
        (m) => m.RequestToJoinPartnerModule,
      ),
  },
  {
    path: 're-invitation/:code',
    loadComponent: () =>
      import('./pages/re-invitation/re-invitation.component').then(
        (m) => m.ReInvitationComponent,
      ),
  },
  {
    path: 'astra/finished',
    loadComponent: () =>
      import('./modules/astra/components/astra-finished-event/astra-finished-event.component').then(
        (m) => m.AstraFinishedEventComponent,
      ),
  },
  {
    path: 'tax-documents',
    loadComponent: () =>
      import('./pages/dashboard/pages/tax-documents/tax-documents.component').then(
        (m) => m.TaxDocumentsComponent,
      ),
  },
  {
    path: 'dashboard/tax-documents',
    redirectTo: 'tax-documents',
  },
  {
    path: 'prequalify',
    loadChildren: () =>
      import('./pages/prequalify/prequalify.module').then(
        (m) => m.PrequalifyModule,
      ),
  },
  {
    path: 'dashboard',
    loadChildren: () =>
      import('./pages/dashboardV2/dashboardV2.module').then(
        (m) => m.DashboardV2Module,
      ),
  },
  {
    path: 'dashboard_old',
    loadChildren: () =>
      import('./pages/dashboard/dashboard.module').then(
        (m) => m.DashboardModule,
      ),
  },
  {
    path: 'confirm-deal/:id',
    loadChildren: () =>
      import('./modules/confirm-deal/confirm-deal.module').then(
        (m) => m.ConfirmDealModule,
      ),
  },
  {
    path: 'apply',
    loadChildren: () =>
      import('./modules/advance-application/advance-application.module').then(
        (m) => m.AdvanceApplicationModule,
      ),
  },
  {
    path: 'cloc',
    loadChildren: () =>
      import('./modules/cloc/cloc.module').then((m) => m.ClocModule),
  },
  {
    path: 'profile',
    loadChildren: () =>
      import('./modules/profile/profile.module').then((m) => m.ProfileModule),
  },
  {
    path: 'invoices',
    loadChildren: () =>
      import('./modules/invoices/invoices.module').then(
        (m) => m.InvoicesModule,
      ),
  },
  {
    path: 'activity',
    loadComponent: () =>
      import('./pages/dashboard/pages/dashboard-activity/dashboard-activity.component').then(
        (m) => m.DashboardActivityComponent,
      ),
  },
  {
    path: 'dashboard/activity',
    redirectTo: 'activity',
  },
  {
    path: 'bank-accounts',
    loadComponent: () =>
      import('./pages/dashboard/pages/dashboard-banks/dashboard-banks.component').then(
        (m) => m.DashboardBanksComponent,
      ),
  },
  {
    path: 'dashboard/bank-accounts',
    redirectTo: 'bank-accounts',
  },
  {
    path: 'deal',
    loadComponent: () =>
      import('./pages/dashboard/pages/dashboard-deal-details/dashboard-deal-details.component').then(
        (m) => m.DashboardDealDetailsComponent,
      ),
  },
  {
    path: 'dashboard/deal',
    redirectTo: 'deal',
  },
  {
    path: 'sign-user-agreement',
    loadComponent: () =>
      import('./pages/dashboard/pages/sign-user-agreement/sign-user-agreement.component').then(
        (m) => m.SignUserAgreementComponent,
      ),
  },
  {
    path: 'dashboard/sign-user-agreement',
    redirectTo: 'sign-user-agreement',
  },
  {
    path: 'new-bank-account',
    component: DashboardNewBankAccountComponent,
  },
  {
    path: 'dashboard/new-bank-account',
    redirectTo: 'new-bank-account',
  },
  {
    path: 'broker',
    component: DashboardBrokerComponent,
  },
  {
    path: 'dashboard/broker',
    redirectTo: 'broker',
  },
  {
    path: 'payroll-settings',
    component: DashboardPayrollSettingsComponent,
  },
  {
    path: 'dashboard/payroll-settings',
    redirectTo: 'payroll-settings',
  },
  {
    path: 'commissions',
    data: {
      type: 'Commissions',
    },
    loadComponent: () =>
      import('./pages/dashboard/pages/dashboard-deals/dashboard-deals.component').then(
        (m) => m.DashboardDealsComponent,
      ),
  },
  {
    path: 'dashboard/commissions',
    redirectTo: 'commissions',
  },
  {
    path: 'commission',
    loadComponent: () =>
      import('./pages/dashboard/pages/dashboard-commission-details/dashboard-commission-details.component').then(
        (m) => m.DashboardCommissionDetailsComponent,
      ),
  },
  {
    path: 'dashboard/commission',
    redirectTo: 'commission',
  },
  {
    path: 'statements',
    data: {
      type: 'Statements',
    },
    loadComponent: () =>
      import('./pages/dashboard/pages/dashboard-deals/dashboard-deals.component').then(
        (m) => m.DashboardDealsComponent,
      ),
  },
  {
    path: 'reporting',
    loadComponent: () =>
      import('./pages/dashboard/pages/dashboard-reporting/dashboard-reporting.component').then(
        (m) => m.DashboardReportingComponent,
      ),
  },
  {
    path: 'dashboard/statements',
    redirectTo: 'statements',
  },
  {
    path: 'taxes',
    loadChildren: () =>
      import('./pages/dashboard/pages/taxes/taxes.module').then(
        (m) => m.TaxesModule,
      ),
  },
  {
    path: 'banking',
    loadChildren: () =>
      import('./pages/dashboard/pages/banking/banking.module').then(
        (m) => m.BankingModule,
      ),
  },
  {
    path: 'pdf-viewer',
    loadComponent: () =>
      import('./components/pdf-viewer/pdf-viewer.component').then(
        (m) => m.PdfViewerComponent,
      ),
  },
];

@NgModule({
  imports: [RouterModule.forRoot(routes)],
  exports: [RouterModule],
})
export class AppRoutingModule {}
